import React, { useState, useEffect } from "react";
import { Box, Stack } from "@mui/material";
import { HiMenuAlt3 } from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import { Link as ScrollLink } from "react-scroll";
import { useNavigate, useLocation } from "react-router-dom";
import { colors, font } from "../theme/tokens";

const navItems = [
  { label: "About", id: "about" },
  { label: "Skills", id: "skills" },
  { label: "Work", id: "work" },
  { label: "Experience", id: "experience" },
  { label: "Contact", id: "contact" },
];

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Navigate to a section; if we're on a sub-page, go home first then scroll.
  const goToSection = (id) => {
    setOpen(false);
    if (onHome) return; // ScrollLink handles it
    navigate("/", { state: { scrollTo: id } });
  };

  const linkStyle = {
    activeClass: "active",
    spy: true,
    smooth: true,
    offset: -90,
    duration: 400,
  };

  const renderLink = (nav, big = false) =>
    onHome ? (
      <ScrollLink key={nav.id} to={nav.id} {...linkStyle} onClick={() => setOpen(false)}>
        <span className="nav-item" style={big ? { fontSize: "22px" } : undefined}>
          {nav.label}
        </span>
      </ScrollLink>
    ) : (
      <span
        key={nav.id}
        className="nav-item"
        style={big ? { fontSize: "22px" } : undefined}
        onClick={() => goToSection(nav.id)}
      >
        {nav.label}
      </span>
    );

  return (
    <>
      {/* Mobile drawer */}
      <Box
        sx={{
          position: "fixed",
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          background: "rgba(11, 17, 32, 0.98)",
          backdropFilter: "blur(10px)",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
          left: open ? "0" : "-100%",
          top: 0,
          transition: "left 0.3s ease-in-out",
          zIndex: 1200,
        }}
      >
        <IoMdClose
          className="icon"
          style={{ position: "absolute", right: 24, top: 24, fontSize: 34 }}
          onClick={() => setOpen(false)}
        />
        <Stack direction="column" spacing={4} alignItems="center">
          {navItems.map((nav) => renderLink(nav, true))}
        </Stack>
      </Box>

      {/* Top bar */}
      <Stack
        direction="row"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          py: scrolled ? 1.5 : 2.5,
          px: { xs: 3, md: 8, lg: 14 },
          alignItems: "center",
          justifyContent: "space-between",
          transition: "all 0.3s ease",
          backdropFilter: "blur(12px)",
          backgroundColor: scrolled ? "rgba(11,17,32,0.82)" : "rgba(11,17,32,0.35)",
          borderBottom: `1px solid ${scrolled ? colors.border : "transparent"}`,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 1 }}
          onClick={() => (onHome ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "9px",
              background: colors.gradPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: font.mono,
              fontWeight: 700,
              color: "#0b1120",
              fontSize: 16,
            }}
          >
            IH
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: colors.ink,
              fontFamily: font.sans,
              display: { xs: "none", sm: "block" },
            }}
          >
            Ibrahim Hamdani
          </Box>
        </Box>

        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
          {navItems.map((nav) => renderLink(nav))}
        </Box>

        <Box sx={{ display: { md: "none" } }}>
          <HiMenuAlt3 className="icon" onClick={() => setOpen(true)} />
        </Box>
      </Stack>
    </>
  );
};

export default NavBar;
